window.Theme = window.Theme || {};


Theme.querySelectorAll = function (selector, context = document) {
  return Array.from(context.querySelectorAll(selector));
};


Theme.URLParams = {
  set: function (key, value) {
    const url = new URL(window.location.href);
    url.searchParams.set(key, value);
    history.replaceState(null, '', url);
  },

  get: function (key) {
    const url = new URL(window.location.href);
    return url.searchParams.get(key);
  },

  delete: function (key) {
    const url = new URL(window.location.href);
    url.searchParams.delete(key);
    history.replaceState(null, '', url);
  },

  has: function (key) {
    const url = new URL(window.location.href);
    return url.searchParams.has(key);
  }
};


Theme.Helpers = {
  preload: function (url) {
    if (!url) {
      return;
    }

    const img = new Image();
    img.src = url;
  },

  prefetch: function (url) {
    if (document.querySelector(`link[href="${url}"]`)) {
      return;
    }

    const link = document.createElement("link");

    link.rel = "prefetch";
    link.href = url;

    document.head.appendChild(link);
  },

  debounce: function (fn, delay = 150) {
    let timeout;

    return function() {
      const context = this;
      const args = arguments;

      clearTimeout(timeout);

      timeout = setTimeout(() => {
        fn.apply(context, args);
      }, delay);
    };
  },

  throttle: function (callback, limit) {
    let lastCall = 0;

    return function () {
      const now = Date.now();

      if (now - lastCall >= limit) {
        lastCall = now;
        callback.apply(this, arguments);
      }
    };
  },

  scroll_to: function (id, offset = 80) {
    const target = document.getElementById(id);

    if (!target) {
      return;
    }

    const position = target.offsetTop - offset;

    window.scrollTo({
      top: position,
      behavior: "smooth"
    });
  }
};


Theme.Component = class extends HTMLElement {
  #elements = null;
  #data = null;
  #events = {};
  #controller = new AbortController();

  constructor() {
    super();
  }

  connectedCallback() {
    this.events();
  }

  get data() {
    if (this.#data !== null) {
      return this.#data;
    }

    const script = this.querySelector(":scope > script[type='application/json']");
    const data = JSON.parse(script?.textContent || "{}");

    return this.#data = data;
  }

  get elements() {
    if (this.#elements !== null) {
      return this.#elements;
    }

    this.#elements = {};
    this.getElements();
    this.getElementCollections();

    return this.#elements;
  }

  getElements() {
    this.querySelectorAll("[data-element]").forEach((element) => {
      const name = element.dataset.element;
      this.#elements[name] = element;
    });
  }

  getElementCollections() {
    this.querySelectorAll("[data-elements]").forEach((element) => {
      const name = element.dataset.elements;
      this.#elements[name] ||= [];
      this.#elements[name].push(element);
    });
  }

  defineLazyElement(name, selector, collection = false) {
    Object.defineProperty(this.#elements, name, {
      configurable: true,
      get: () => {
        let element = null;

        if (collection) {
          element = Array.from(this.querySelectorAll(selector));
        } else {
          element = this.querySelector(selector);
        }

        Object.defineProperty(this.#elements, name, { value: element });

        return element;
      }
    });
  }

  defineElements(map = {}) {
    this.#elements ||= {};

    for (const [name, selector] of Object.entries(map)) {
      this.defineLazyElement(name, selector, false);
    }
  }

  defineElementCollections(map = {}) {
    this.#elements ||= {};

    for (const [name, selector] of Object.entries(map)) {
      this.defineLazyElement(name, selector, true);
    }
  }

  events() {
    this.getActionAttributeEvents();
    this.attachDelegatedEvents();
  }

  getActionAttributeEvents() {
    const elements = this.querySelectorAll("[data-action]");

    elements.forEach((element) => {
      const [type, method] = element.dataset.action.split(":");
      const detail = JSON.parse(element.dataset.detail || "{}");

      this.addDelegatedEvent(type, element, (event) => {
        this[method](detail, element, event);
      });
    });
  }

  addEvent(type, target, listener) {
    target.addEventListener(type, (event) => {
      listener.call(this, event);
    }, { signal: this.#controller.signal });
  }

  addDelegatedEvent(type, target, listener) {
    this.#events[type] ||= [];
    this.#events[type].push({ target, listener });
  }

  attachDelegatedEvents() {
    for (const [type, listeners] of Object.entries(this.#events)) {
      this.addEventListener(type, (event) => {
        listeners.forEach(({ target, listener }) => {
          if (this.closestByElement(event.target, target)) {
            listener.call(this, event);
            event.stopPropagation();
          }
        });
      }, { signal: this.#controller.signal });
    }
  }

  closestByElement(starting, target) {
    let current = starting;

    while (current) {
      if (current === target) {
        return current;
      }

      current = current.parentElement;
    }

    return null;
  }

  disconnectedCallback() {
    this.#controller.abort();
  }
}


Theme.ComponentCollection = class {
  #elements = null;

  constructor(selector) {
    this.define(selector);
  }

  define(selector) {
    Object.defineProperty(this, "elements", {
      get: () => {
        return this.#elements || (this.#elements = Theme.querySelectorAll(selector));
      }
    });
  }
};

Theme.ComponentCollections = {};
